import { useCallback, useEffect, useRef, useState } from "react"

interface UseAttemptTimer {
  /** Segundos restantes (inteiro, >= 0). null enquanto não inicializado. */
  remaining: number | null
  /** Ressincroniza o cronômetro com o valor autoritativo do servidor. */
  sync: (serverSeconds: number) => void
}

/**
 * Cronômetro regressivo baseado em deadline absoluto (imune a drift de setInterval
 * e a abas em background). Dispara onExpire exatamente uma vez ao zerar.
 */
export function useAttemptTimer(initialSeconds: number | null, onExpire: () => void): UseAttemptTimer {
  const deadlineRef = useRef<number | null>(null)
  const expiredRef = useRef(false)
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  const [remaining, setRemaining] = useState<number | null>(
    initialSeconds === null ? null : Math.max(0, Math.floor(initialSeconds))
  )

  const sync = useCallback((serverSeconds: number) => {
    deadlineRef.current = Date.now() + serverSeconds * 1000
    setRemaining(Math.max(0, Math.floor(serverSeconds)))
  }, [])

  useEffect(() => {
    if (initialSeconds === null) return
    if (deadlineRef.current === null) {
      deadlineRef.current = Date.now() + initialSeconds * 1000
      setRemaining(Math.max(0, Math.floor(initialSeconds)))
    }
  }, [initialSeconds])

  useEffect(() => {
    const id = window.setInterval(() => {
      if (deadlineRef.current === null) return
      const secs = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
      setRemaining(secs)
      if (secs <= 0 && !expiredRef.current) {
        expiredRef.current = true
        onExpireRef.current()
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [])

  return { remaining, sync }
}
